import { useState } from 'react';
import { useRoom } from '@features/room';
import { Panel, ActionButton, Toggle } from '@shared/ui';
import { PlayIcon, StopIcon } from '@shared/ui';

/**
 * RoomControls — конференц-панель для страницы Streaming.
 *
 * Отвечает ТОЛЬКО за организацию LiveKit-комнаты:
 *  - создать / войти в комнату по имени
 *  - показать и скопировать share-ссылку
 *  - тумблеры mic / cam
 *  - покинуть комнату
 *
 * НЕ отвечает за платформенную трансляцию (YouTube / Twitch / RTMP). Это
 * отдельный bounded context (`features/streaming` ↔ `usePlatformStreamStore`)
 * и на этой странице его нет — здесь только co-host сценарий.
 */
function generateRoomName(): string {
  const adjectives = ['swift', 'bold', 'calm', 'keen', 'warm'];
  const nouns = ['river', 'peak', 'forge', 'orbit', 'spark'];
  const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const num = Math.floor(Math.random() * 900) + 100;
  return `${adj}-${noun}-${num}`;
}

export function RoomControls() {
  const {
    connectionState,
    room,
    localParticipant,
    participants,
    isJoining,
    joinError,
    createAndJoin,
    joinByLink,
    leave,
    setMicEnabled,
    setCameraEnabled,
    settings,
    updateSettings,
  } = useRoom();

  const [linkInput, setLinkInput] = useState('');
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [copied, setCopied] = useState(false);

  const isConnected = connectionState === 'connected';
  const isConnecting = connectionState === 'connecting' || isJoining;
  const hasName = settings.participantName.trim().length > 0;

  const handleCreate = () => {
    if (!hasName) return;
    const name = generateRoomName();
    void createAndJoin(name);
  };

  const handleJoin = () => {
    if (!hasName) return;
    const val = linkInput.trim();
    if (!val) return;
    void joinByLink(val).then(() => {
      setShowJoinForm(false);
    });
  };

  const handleCopyLink = async () => {
    if (room?.shareUrl) {
      await navigator.clipboard.writeText(room.shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleLeave = async () => {
    await leave();
  };

  return (
    <Panel title="ROOM">
      {/* Connection status bar */}
      <div className="mb-4 flex items-center justify-between rounded-lg border border-surface-border bg-surface-2 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span
            className={`h-2 w-2 rounded-full ${isConnected
              ? 'bg-status-success'
              : isConnecting
                ? 'animate-pulse bg-status-warning'
                : 'bg-surface-3'
              }`}
          />
          <span className="text-sm text-text-secondary">
            {isConnected
              ? room?.name ?? 'Connected'
              : isConnecting
                ? 'Connecting…'
                : 'Not in a room'}
          </span>
        </div>
        <span className="text-xs text-text-muted">
          {isConnected ? `${participants.length} participant${participants.length !== 1 ? 's' : ''}` : ''}
        </span>
      </div>

      {/* Participant name */}
      {!isConnected && (
        <div className="mb-4">
          <label className="mb-1.5 block text-xs text-text-secondary">Your name</label>
          <input
            type="text"
            value={settings.participantName}
            onChange={(e) => updateSettings({ participantName: e.target.value })}
            placeholder="Enter your name"
            className="w-full rounded-lg border border-surface-border bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder-text-muted outline-none focus:border-accent"
          />
        </div>
      )}

      {/* Action buttons */}
      {!isConnected ? (
        <div className="flex flex-col gap-3">
          <ActionButton
            variant="accent"
            icon={<PlayIcon className="h-4 w-4" />}
            onClick={handleCreate}
            disabled={isConnecting || !hasName}
          >
            {isConnecting ? 'Connecting…' : 'Create Room'}
          </ActionButton>

          {!showJoinForm ? (
            <ActionButton
              variant="neutral"
              onClick={() => setShowJoinForm(true)}
            >
              Join via Link
            </ActionButton>
          ) : (
            <div className="flex gap-2">
              <input
                autoFocus
                type="text"
                value={linkInput}
                onChange={(e) => setLinkInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
                placeholder="Paste invite link"
                className="flex-1 rounded-lg border border-surface-border bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder-text-muted outline-none focus:border-accent"
              />
              <button
                type="button"
                onClick={handleJoin}
                disabled={!hasName}
                className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                Join
              </button>
            </div>
          )}

          {!hasName && (
            <p className="text-xs text-text-muted">Enter your name to create or join a room.</p>
          )}

          {joinError && (
            <p className="rounded-lg border border-status-live/30 bg-status-live/10 px-3 py-2 text-xs text-status-live">
              {joinError}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {/* Share link */}
          {room && (
            <div className="rounded-lg border border-surface-border bg-surface-2 p-3">
              <div className="mb-2 text-xs text-text-muted">Share link</div>
              <div className="flex items-center gap-2">
                <span className="flex-1 truncate font-mono text-xs text-text-secondary">
                  {room.shareUrl}
                </span>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="shrink-0 rounded-md border border-surface-border bg-surface-3 px-3 py-1.5 text-xs text-text-primary hover:bg-accent hover:text-white hover:border-accent transition-colors"
                >
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          {/* Mic/Cam toggles */}
          {localParticipant && (
            <div className="flex gap-4">
              <Toggle
                checked={localParticipant.isMicEnabled}
                onChange={(enabled) => void setMicEnabled(enabled)}
                label="Microphone"
              />
              <Toggle
                checked={localParticipant.isCameraEnabled}
                onChange={(enabled) => void setCameraEnabled(enabled)}
                label="Camera"
              />
            </div>
          )}

          {/* Leave */}
          <ActionButton
            variant="danger"
            icon={<StopIcon className="h-4 w-4" />}
            onClick={() => void handleLeave()}
          >
            Leave Room
          </ActionButton>
        </div>
      )}
    </Panel>
  );
}
